import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

import { useSession } from '@/lib/useSession';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { formatDateTime } from '@/lib/dateUtils';
import { DataGridShell } from '@/components/grid/DataGridShell';
import {
  hasFaireAdmin,
  hasFaireViewer,
  relativeTime,
  useFaireErrors,
  type FaireError,
  type FaireErrorActionKind,
} from '@/lib/useFaireRuns';
import { MapCustomerModal } from './MapCustomerModal';

// Brand-portal URL for a Faire order. Faire's portal expects the lowercase
// order ID, which is the value we store as faire_order_id throughout
// Cockpit. The path is the same across all retailers and brands.
function faireOrderUrl(orderId: string): string {
  return `https://www.faire.com/brand-portal/orders/${orderId.toLowerCase()}`;
}

// JSM portal URL for a Tell-IT click. The IT service desk portal id is 2
// (IT Service Desk in hd.jira.ampersandbrands.com). Pre-fills the summary
// so Inside Sales doesn't have to retype the SKU.
function jsmCasePackUrl(sku: string | null): string {
  const summary = sku
    ? `Faire-Sage: confirm case-pack for SKU ${sku}`
    : 'Faire-Sage: confirm case-pack for an unknown SKU';
  const description = sku
    ? `Cockpit Issues page flagged that SKU ${sku} has an unknown case-pack. Please confirm with the warehouse and reply with how many units come in one case so IT can load the override.`
    : 'Cockpit Issues page flagged an unknown case-pack but did not capture the SKU. Pull the latest pipefile_emit error from the Issues page for context.';
  const params = new URLSearchParams({ summary, description });
  return `https://hd.jira.ampersandbrands.com/servicedesk/customer/portal/2?${params.toString()}`;
}

function actionLabel(kind: FaireErrorActionKind): string {
  switch (kind) {
    case 'map_customer':
      return 'Map customer';
    case 'pick_customer':
      return 'Pick one';
    case 'confirm_case_pack':
      return 'Tell IT';
    case 'reauth_faire':
      return 'IT in progress';
    case 'view_in_faire':
      return 'Open in Faire';
    case 'wait_for_it':
      return 'IT in progress';
    default:
      return 'Review';
  }
}

function matchesSearch(row: FaireError, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    (row.faire_order_id ?? '').toLowerCase().includes(needle) ||
    (row.friendly_title ?? '').toLowerCase().includes(needle) ||
    (row.action_hint ?? '').toLowerCase().includes(needle) ||
    (row.error_message ?? '').toLowerCase().includes(needle)
  );
}

function ActionButton({
  row,
  onMapCustomer,
}: {
  row: FaireError;
  onMapCustomer: (row: FaireError) => void;
}) {
  // Map / Pick: open the in-app MapCustomerModal. Requires admin to actually
  // submit; the button stays clickable for viewers so they can see the modal
  // contents, but the modal's Confirm will 403 if they're not an admin.
  if (row.action_kind === 'map_customer' || row.action_kind === 'pick_customer') {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMapCustomer(row);
        }}
        className="inline-flex items-center gap-1 text-xs border border-amp-olive rounded px-2 py-1 text-amp-olive hover:bg-amp-cream-warm dark:border-amp-olive-dark dark:text-amp-olive-dark dark:hover:bg-amp-dark-surface-warm"
      >
        {actionLabel(row.action_kind)}
      </button>
    );
  }

  // Tell IT: open a JSM portal create-ticket URL pre-filled with the SKU.
  if (row.action_kind === 'confirm_case_pack') {
    return (
      <a
        href={jsmCasePackUrl(row.context.sku)}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-2 py-1 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {actionLabel(row.action_kind)}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  // Open in Faire: only meaningful if we have an order id.
  if (row.action_kind === 'view_in_faire' && row.faire_order_id) {
    return (
      <a
        href={faireOrderUrl(row.faire_order_id)}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-2 py-1 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {actionLabel(row.action_kind)}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  // Reauth / wait / unknown: static label.
  return (
    <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
      {actionLabel(row.action_kind)}
    </span>
  );
}

export function FaireErrors() {
  const { user } = useSession();
  const isViewer = hasFaireViewer(user?.groups, user?.superAdmin);
  const isAdmin = hasFaireAdmin(user?.groups, user?.superAdmin);

  const [resolvedFilter, setResolvedFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [mapRow, setMapRow] = useState<FaireError | null>(null);

  const filters = useMemo(
    () => ({
      resolved: resolvedFilter === 'open' ? (0 as const) : resolvedFilter === 'resolved' ? (1 as const) : undefined,
      limit: 100,
      offset: page * 100,
    }),
    [resolvedFilter, page],
  );
  const errorsQ = useFaireErrors(filters);

  const filteredRows = useMemo(
    () => errorsQ.errors.filter((r) => matchesSearch(r, search)),
    [errorsQ.errors, search],
  );

  if (!isViewer) {
    return (
      <div>
        <Link
          to="/sales/faire/dashboard"
          className="text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink inline-flex items-center gap-1 mb-3"
        >
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Issues</h1>
        </div>
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            You don't have access to Faire-Sage. Contact IT to request the cockpit-faire-sage-viewer role.
          </p>
        </div>
      </div>
    );
  }

  const toolbar = (
    <div className="flex flex-wrap gap-3">
      <div>
        <label htmlFor="resolved" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Status
        </label>
        <select
          id="resolved"
          value={resolvedFilter}
          onChange={(e) => {
            setResolvedFilter(e.target.value as 'all' | 'open' | 'resolved');
            setPage(0);
          }}
          className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
      </div>
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Search (retailer, order ID, issue text)
        </label>
        <input
          id="search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="bumbelou, customer, case-pack..."
          className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between">
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Page {page + 1}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={errorsQ.errors.length < 100}
          className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Load more
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link
        to="/sales/faire/dashboard"
        className="text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink inline-flex items-center gap-1 mb-3 shrink-0"
      >
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>
      <div className="mb-4 shrink-0">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Issues</h1>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Orders that need someone's attention. Each row shows what happened and what to do next.
        </p>
      </div>

      <DataGridShell
        loaded={filteredRows.length}
        countNoun="issues"
        toolbar={toolbar}
        footer={footer}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="text-left font-medium px-4 py-2 w-[110px]">When</th>
              <th className="text-left font-medium px-4 py-2">What happened</th>
              <th className="text-left font-medium px-4 py-2 w-[140px]">Faire order</th>
              <th className="text-left font-medium px-4 py-2 w-[120px]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {errorsQ.isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading issues...
                </td>
              </tr>
            ) : errorsQ.error ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-red-700 dark:text-red-300">
                  Could not load issues: {errorsQ.error.message}
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No issues to review.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const isOpen = expandedId === row.error_id;
                return (
                  <>
                    <tr
                      key={row.error_id}
                      className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm cursor-pointer"
                      onClick={() => setExpandedId(isOpen ? null : row.error_id)}
                    >
                      <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          {relativeTime(row.occurred_at)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                        <div className="font-medium">{row.friendly_title}</div>
                        <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-0.5">
                          {row.action_hint}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {row.faire_order_id ? (
                          <a
                            href={faireOrderUrl(row.faire_order_id)}
                            target="_blank"
                            rel="noreferrer noopener"
                            onClick={(e) => e.stopPropagation()}
                            className="text-amp-olive dark:text-amp-olive-dark hover:underline inline-flex items-center gap-1"
                          >
                            {row.faire_order_id}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                        )}
                      </td>
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <ActionButton row={row} onMapCustomer={(r) => setMapRow(r)} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${row.error_id}-detail`} className="bg-amp-cream-warm/40 dark:bg-amp-dark-surface-warm/40">
                        <td colSpan={4} className="px-6 py-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                            <div>
                              <span className="font-medium text-amp-ink dark:text-amp-dark-ink">Status:</span>{' '}
                              {row.resolved ? 'resolved' : 'open'}
                            </div>
                            <div>
                              <span className="font-medium text-amp-ink dark:text-amp-dark-ink">Occurred:</span>{' '}
                              {formatDateTime(row.occurred_at)}
                            </div>
                            {isAdmin && (
                              <>
                                <div>
                                  <span className="font-medium text-amp-ink dark:text-amp-dark-ink">Run ID:</span>{' '}
                                  <span className="font-mono">{row.run_id ?? '--'}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-amp-ink dark:text-amp-dark-ink">Stage:</span>{' '}
                                  {row.stage}
                                </div>
                                <div className="md:col-span-2">
                                  <span className="font-medium text-amp-ink dark:text-amp-dark-ink">Raw:</span>{' '}
                                  <span className="font-mono break-all">{row.error_message ?? ''}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </DataGridShell>

      <MapCustomerModal
        open={mapRow !== null}
        onClose={() => setMapRow(null)}
        errorRow={mapRow}
      />
    </div>
  );
}
